const TOKEN_KEY = 'capstone.token';

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? import.meta.env.VITE_API_BEARER_TOKEN ?? '';
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`${status} ${body}`);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${getToken()}`,
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401 && localStorage.getItem(TOKEN_KEY) !== null) {
    // Stored token was rejected (rotated, typo, etc). Drop it so the gate
    // re-renders the Login screen instead of stranding the user on Loading.
    clearToken();
    window.location.reload();
    throw new ApiError(401, 'token rejected — reloading');
  }
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}

/**
 * Validate a candidate bearer token by hitting a cheap authed endpoint.
 * Does NOT mutate localStorage — caller decides what to do on success.
 */
export async function probeToken(candidate: string): Promise<'ok' | 'invalid' | 'network'> {
  try {
    const res = await fetch('/api/employees', {
      headers: { authorization: `Bearer ${candidate}` },
    });
    if (res.ok) return 'ok';
    if (res.status === 401) return 'invalid';
    return 'network';
  } catch {
    return 'network';
  }
}
